import { defineStore } from "pinia";
import type { LocationQuery, LocationQueryRaw } from "vue-router";

import { MerchantApiError } from "../authentication/errors.js";
import {
  defaultAuditFilters,
  toAuditFilters,
  type AuditDetailViewModel,
  type AuditFiltersViewModel,
  type AuditPageViewModel,
  type AuditSectionStateViewModel,
} from "./models.js";
import type { AuditService } from "./service.js";

export const useAuditStore = defineStore("merchant-audit", {
  state: (): {
    appliedFilters: AuditFiltersViewModel;
    detailError?: string;
    detailRequestId?: string;
    draftFilters: AuditFiltersViewModel;
    listError?: string;
    listRequestId?: string;
    loadingDetail: boolean;
    loadingList: boolean;
    pageView: AuditPageViewModel;
    selectedAuditId?: string;
    selectedEntry?: AuditDetailViewModel;
    service?: AuditService;
  } => ({
    appliedFilters: defaultAuditFilters(),
    draftFilters: defaultAuditFilters(),
    loadingDetail: false,
    loadingList: false,
    pageView: emptyAuditPageViewModel(),
  }),
  actions: {
    configure(service: AuditService): void {
      this.service = service;
    },
    async loadInitialFromRoute(routeQuery: LocationQuery): Promise<void> {
      const filters = this.requireService().filtersFromRouteQuery(routeQuery);
      this.draftFilters = { ...filters };
      this.appliedFilters = { ...filters };
      this.setSelectedAuditId(readSelectedAuditId(routeQuery));
      await this.fetchAppliedList();
      await this.loadSelectedFromRouteWhenNeeded();
    },
    updateDraftFilter<TKey extends keyof AuditFiltersViewModel>(
      key: TKey,
      value: AuditFiltersViewModel[TKey],
    ): void {
      this.draftFilters = { ...this.draftFilters, [key]: value };
    },
    async applyFilters(): Promise<LocationQueryRaw> {
      this.appliedFilters = withoutCursor(this.draftFilters);
      delete this.selectedAuditId;
      await this.fetchAppliedList();
      return this.routeQuery();
    },
    async clearFilters(): Promise<LocationQueryRaw> {
      this.draftFilters = defaultAuditFilters();
      this.appliedFilters = defaultAuditFilters();
      delete this.selectedAuditId;
      await this.fetchAppliedList();
      return this.routeQuery();
    },
    async clearCursorAndRetry(): Promise<LocationQueryRaw> {
      this.appliedFilters = withoutCursor(this.appliedFilters);
      this.draftFilters = { ...this.appliedFilters };
      await this.fetchAppliedList();
      return this.routeQuery();
    },
    async loadNextPage(): Promise<void> {
      const nextCursor = this.pageView.pagination.nextCursor;
      if (nextCursor === null) return;
      await this.fetchNextPage(nextCursor);
    },
    async retry(): Promise<void> {
      await this.fetchAppliedList();
    },
    async selectEntry(auditId: string): Promise<LocationQueryRaw> {
      this.selectedAuditId = auditId;
      this.applySelectedEntryFromPage(auditId);
      await this.fetchSelectedEntry(auditId);
      return this.routeQuery();
    },
    routeQuery(): LocationQueryRaw {
      return this.requireService().routeQueryFromFilters(this.appliedFilters, this.selectedAuditId);
    },
    async fetchAppliedList(): Promise<void> {
      this.loadingList = true;
      delete this.listError;
      delete this.listRequestId;
      try {
        const requestedSelection = this.selectedAuditId;
        this.pageView = await this.requireService().loadAudit(
          toAuditFilters(this.appliedFilters),
          this.draftFilters,
        );
        if (requestedSelection === undefined) {
          delete this.selectedEntry;
          delete this.selectedAuditId;
        } else {
          this.setSelectedEntry(this.pageView.selectedEntry);
          this.selectedAuditId = requestedSelection;
        }
      } catch (error: unknown) {
        this.listError = messageForError(error, "Audit history could not be loaded.");
        if (error instanceof MerchantApiError) {
          this.listRequestId = error.requestId;
        }
        this.pageView = {
          ...this.pageView,
          sectionStates: [errorSection("table", error, this.listError)],
        };
      } finally {
        this.loadingList = false;
      }
    },
    async fetchNextPage(cursor: string): Promise<void> {
      this.loadingList = true;
      delete this.listError;
      delete this.listRequestId;
      try {
        const previousPage = this.pageView;
        const nextPage = await this.requireService().loadAudit(
          toAuditFilters({ ...withoutCursor(this.appliedFilters), cursor }),
          this.draftFilters,
        );
        this.pageView = appendedAuditPage(previousPage, nextPage);
      } catch (error: unknown) {
        this.listError = messageForError(error, "More audit history could not be loaded.");
        if (error instanceof MerchantApiError) {
          this.listRequestId = error.requestId;
        }
        this.pageView = {
          ...this.pageView,
          sectionStates: [errorSection("table", error, this.listError)],
        };
      } finally {
        this.loadingList = false;
      }
    },
    async fetchSelectedEntry(auditId: string): Promise<void> {
      this.loadingDetail = true;
      delete this.detailError;
      delete this.detailRequestId;
      try {
        const entry = await this.requireService().loadAuditEntry(auditId);
        this.setSelectedEntry(entry);
        this.pageView = { ...this.pageView, selectedEntry: entry };
      } catch (error: unknown) {
        this.detailError = messageForError(error, "Audit entry could not be loaded.");
        if (error instanceof MerchantApiError) {
          this.detailRequestId = error.requestId;
        }
        this.pageView = {
          ...this.pageView,
          sectionStates: [
            ...this.pageView.sectionStates.filter((state) => state.id !== "detail"),
            errorSection("detail", error, this.detailError),
          ],
        };
      } finally {
        this.loadingDetail = false;
      }
    },
    applySelectedEntryFromPage(auditId: string): void {
      const rowExists = this.pageView.rows.some((row) => row.id === auditId);
      if (!rowExists) return;
      if (this.pageView.selectedEntry?.id === auditId)
        this.setSelectedEntry(this.pageView.selectedEntry);
    },
    clearSelectedEntry(): void {
      delete this.selectedAuditId;
      delete this.selectedEntry;
      delete this.detailError;
      delete this.detailRequestId;
    },
    async loadSelectedFromRouteWhenNeeded(): Promise<void> {
      if (this.selectedAuditId === undefined) return;
      await this.fetchSelectedEntry(this.selectedAuditId);
    },
    requireService(): AuditService {
      if (this.service === undefined) throw new Error("Audit store was not configured before use.");
      return this.service;
    },
    setSelectedAuditId(auditId: string | undefined): void {
      if (auditId === undefined) {
        delete this.selectedAuditId;
        return;
      }
      this.selectedAuditId = auditId;
    },
    setSelectedEntry(entry: AuditDetailViewModel | undefined): void {
      if (entry === undefined) {
        delete this.selectedEntry;
        return;
      }
      this.selectedEntry = entry;
    },
  },
});

function readSelectedAuditId(query: LocationQuery): string | undefined {
  const value = query.auditId;
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function withoutCursor(filters: AuditFiltersViewModel): AuditFiltersViewModel {
  const { cursor: _cursor, ...rest } = filters;
  return rest;
}

function appendedAuditPage(
  previousPage: AuditPageViewModel,
  nextPage: AuditPageViewModel,
): AuditPageViewModel {
  return {
    ...nextPage,
    appliedFilters: withoutCursor(previousPage.appliedFilters),
    draftFilters: previousPage.draftFilters,
    rows: [...previousPage.rows, ...nextPage.rows],
    sectionStates:
      previousPage.rows.length > 0 && nextPage.rows.length === 0 ? [] : nextPage.sectionStates,
    ...(previousPage.selectedEntry === undefined
      ? {}
      : { selectedEntry: previousPage.selectedEntry }),
  };
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.kind === "access")
    return "You do not have permission to read audit history.";
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function errorSection(
  id: "table" | "detail",
  error: unknown,
  message: string,
): AuditSectionStateViewModel {
  if (error instanceof MerchantApiError) {
    return {
      id,
      status:
        error.kind === "access"
          ? "PERMISSION_DENIED"
          : error.code.includes("STALE_CURSOR")
            ? "STALE_CURSOR"
            : error.code.endsWith("NOT_FOUND")
              ? "NOT_FOUND"
              : "FAILED",
      message,
      requestId: error.requestId,
    };
  }

  return { id, status: "FAILED", message };
}

function emptyAuditPageViewModel(): AuditPageViewModel {
  const filters = defaultAuditFilters();
  return {
    appliedFilters: filters,
    draftFilters: filters,
    rows: [],
    pagination: { nextCursor: null, canGoNext: false, staleCursor: false },
    sectionStates: [],
  };
}
